import {useTranslation} from "react-i18next";
import {useQuery} from "@tanstack/react-query";
import {Avatar, AvatarFallback, AvatarImage} from "@/components/ui/avatar.tsx";
import {Badge} from "@/components/ui/badge.tsx";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "@/components/ui/hover-card";
import ExpandableText from "@/components/ExpandableText.tsx";
import {LucideMail} from "lucide-react";
import {getImgSrc} from "@/utils/file-upload.ts";
import {getLocalizedFieldName} from "@/utils/translation-helper.ts";
import {getMatches} from "@/utils/suggestion-helper.ts";
import {SuggestedTo} from "@/types/Suggestion.ts";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getItemsOfAllTypes} from "@/services/itemService.ts";

interface MatchedMenteeCardProps {
    suggestions: SuggestedTo[];
    mentorAffinityGroupId?: number | null;
}

const MatchedMenteeCard = ({suggestions, mentorAffinityGroupId}: MatchedMenteeCardProps) => {
    const {t} = useTranslation();

    const {data: criteria} = useQuery({
        queryKey: queryKeys.fetchCriteria.all,
        queryFn: () => getItemsOfAllTypes(),
    });

    return (
        <ul>
            {suggestions.map((suggestedTo, idx: number) => {
                // Handle menteePreferences array (API returns array, but type might say singular)
                const menteeData = suggestedTo.mentee as any;
                const menteePreference = Array.isArray(menteeData?.menteePreferences) && menteeData.menteePreferences.length > 0
                    ? menteeData.menteePreferences[0]
                    : menteeData?.menteePreference || null;
                
                const {broadMatched, specificMatched} = getMatches(
                    criteria,
                    suggestedTo.brScore || 0,
                    suggestedTo.spScore || 0,
                    menteePreference
                );
                
                // Construct display name from firstName/lastName (API may not include fullName)
                const user = suggestedTo.mentee.user || {};
                const displayName = user.fullName 
                    ?? ([user.firstName, user.lastName]
                        .filter(Boolean)
                        .join(" ")
                        .trim() || "Unknown");

                return (
                    <li key={idx} className="mb-6 last:mb-0">
                        <div className="rounded-xl border border-border bg-bg-card p-6 shadow-sm transition-colors sm:flex sm:items-start sm:gap-6">
                            <div className="flex flex-col items-center sm:items-start flex-shrink-0 mb-4 sm:mb-0 w-full sm:w-28">
                                <Avatar className="h-28 w-28 rounded-xl border-2 border-border mb-3">
                                    <AvatarImage
                                        src={getImgSrc(suggestedTo.mentee.user.profilePictureSrc) ?? import.meta.env.VITE_DEFAULT_AVATAR}
                                        alt={displayName}
                                        className="object-cover"
                                    />
                                    <AvatarFallback className="rounded-xl bg-accent/15 text-2xl font-semibold text-text-1">
                                        {displayName && displayName.length > 0 ? displayName.charAt(0).toUpperCase() : "?"}
                                    </AvatarFallback>
                                </Avatar>
                            </div>
                            <div className="flex-grow min-w-0 w-full sm:text-left text-center">
                                <div className="mb-4">
                                    <HoverCard>
                                        <HoverCardTrigger asChild>
                                            <a
                                                href={`/mentees/${suggestedTo.mentee.id}`}
                                                className="text-xl font-semibold text-text-1 hover:text-springGreen transition-colors underline-offset-4 hover:underline inline-block"
                                            >
                                                {displayName}
                                            </a>
                                        </HoverCardTrigger>
                                        <HoverCardContent align="start" className="w-80 rounded-xl border border-border bg-bg-card">
                                            <div className="flex gap-4">
                                                <Avatar className="h-12 w-12 rounded-xl border border-border flex-shrink-0">
                                                    <AvatarImage src={getImgSrc(suggestedTo.mentee.user.profilePictureSrc)} className="object-cover" />
                                                    <AvatarFallback className="rounded-xl bg-accent/15 text-text-1">
                                                        {user.firstName?.charAt(0)?.toUpperCase() || displayName.charAt(0)?.toUpperCase() || "?"}
                                                    </AvatarFallback>
                                                </Avatar>
                                                <div className="space-y-1 flex-1 min-w-0">
                                                    <h4 className="font-semibold text-base text-text-1">{displayName}</h4>
                                                    <div className="flex items-center gap-2">
                                                        <LucideMail className="h-4 w-4 text-text-3 flex-shrink-0" />
                                                        <span className="text-xs text-text-2 truncate">{user.email || ""}</span>
                                                    </div>
                                                    <p className="text-sm text-text-2 pt-2">
                                                        {(() => {
                                                            const studentStatus = menteeData?.[getLocalizedFieldName("studentStatus") as string]?.toString();
                                                            const discipline = menteeData?.[getLocalizedFieldName("discipline") as string]?.toString();
                                                            const institution = menteeData?.[getLocalizedFieldName("institution") as string]?.toString();
                                                            const parts = [studentStatus, discipline, institution].filter(Boolean);
                                                            return parts.length > 0 ? parts.join(", ") : "";
                                                        })()}
                                                    </p>
                                                </div>
                                            </div>
                                        </HoverCardContent>
                                    </HoverCard>
                                </div>
                                {(() => {
                                    const bio = menteeData?.[getLocalizedFieldName("bio") as string]?.toString();
                                    return bio ? (
                                        <div className="mb-4">
                                            <ExpandableText text={bio} maxLength={300} />
                                        </div>
                                    ) : null;
                                })()}
                                {(() => {
                                    const affinityGroups = menteePreference?.affinityGroups ?? [];
                                    const hasAffinityGroups = affinityGroups.length > 0;
                                    const hasBroad = broadMatched.length > 0;
                                    const hasSpecific = specificMatched.length > 0;
                                    if (!hasAffinityGroups && !hasBroad && !hasSpecific) return null;
                                    return (
                                        <div className="space-y-3 pt-4 border-t border-border">
                                            {hasAffinityGroups && (
                                                <div className="space-y-2">
                                                    <span className="text-sm font-semibold text-text-1 block">
                                                        {t("affinity.group")}:
                                                    </span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {affinityGroups.map((item: any, index: number) => {
                                                            const affinityGroupName = item.affinityGroup[getLocalizedFieldName("name") as keyof typeof item.affinityGroup] as string;
                                                            const isMatch = mentorAffinityGroupId != null && mentorAffinityGroupId === item.affinityGroup.id;
                                                            return (
                                                                <Badge
                                                                    key={index}
                                                                    variant="outline"
                                                                    className={`rounded-full px-3 py-1 text-sm font-normal border-0 transition-[filter,box-shadow] duration-150 ${
                                                                        isMatch
                                                                            ? "bg-status-success text-white hover:brightness-110 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.35)]"
                                                                            : "bg-status-danger text-white hover:brightness-110"
                                                                    }`}
                                                                >
                                                                    {affinityGroupName}
                                                                </Badge>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            )}
                                            {hasBroad && (
                                                <div className="space-y-2">
                                                    <span className="text-sm font-semibold text-text-1 block">{t("criteria.broadCriteria")}:</span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {broadMatched.map((item, index) => (
                                                            <Badge
                                                                key={index}
                                                                variant="outline"
                                                                className={`rounded-full px-3 py-1 text-sm font-normal border-0 transition-[filter,box-shadow] duration-150 ${
                                                                    item.match
                                                                        ? "bg-status-success text-white hover:brightness-110 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.35)]"
                                                                        : "bg-status-danger text-white hover:brightness-110"
                                                                }`}
                                                            >
                                                                {t(`criteria.${item.key}`)} → {item.value}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                            {hasSpecific && (
                                                <div className="space-y-2">
                                                    <span className="text-sm font-semibold text-text-1 block">{t("criteria.specificCriteria")}:</span>
                                                    <div className="flex flex-wrap gap-2">
                                                        {specificMatched.map((item, index) => (
                                                            <Badge
                                                                key={index}
                                                                variant="outline"
                                                                className={`rounded-full px-3 py-1 text-sm font-normal border-0 transition-[filter,box-shadow] duration-150 ${
                                                                    item.match
                                                                        ? "bg-status-success text-white hover:brightness-110 hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.35)]"
                                                                        : "bg-status-danger text-white hover:brightness-110"
                                                                }`}
                                                            >
                                                                {item.keys.length === 1 ? (
                                                                    <>{t(`criteria.${item.keys[0]}`)} → {item.values[0]}</>
                                                                ) : (
                                                                    <>{t(`criteria.${item.keys[0]}`)} → {item.values[0]} & {t(`criteria.${item.keys[1]}`)} → {item.values[1]}</>
                                                                )}
                                                            </Badge>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </li>
                );
            })}
        </ul>
    );
};

export default MatchedMenteeCard;
